// Utility functions for assessment operations

/**
 * Shuffles an array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} - Shuffled array
 */
export const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/**
 * Creates a response array with a single selected response
 * @param {number} selectedIndex - Index of selected response
 * @param {number} totalLength - Total length of response array
 * @returns {Array} - Response array with selected index set to 1
 */
export const createResponseArray = (selectedIndex, totalLength = 40) => {
  const responses = new Array(totalLength).fill(0);
  responses[selectedIndex] = 1;
  return responses;
};

/**
 * Validates if all seats are filled in the restaurant simulation
 * @param {Array} imgIndex - Array representing seat assignments
 * @returns {boolean} - True if all seats are filled
 */
export const areAllSeatsFilled = (imgIndex) => {
  return imgIndex.every(seat => seat !== undefined);
};

/**
 * Calculates progress percentage based on current module
 * @param {number} currentModule - Current module index
 * @param {number} totalModules - Total number of modules
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateProgress = (currentModule, totalModules = 37) => {
  return Math.round(((currentModule + 1) / totalModules) * 100);
};

/**
 * Gets progress step number based on current module
 * @param {number} currentModule - Current module index
 * @returns {number} - Progress step number
 */
export const getProgressStep = (currentModule) => {
  return Math.floor((currentModule + 1) / 5) + 1;
};

/**
 * Validates phone number format
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid phone number format
 */
export const validatePhoneNumber = (phoneNumber) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phoneNumber.replace(/\s/g, ''));
};

/**
 * Formats phone number for display
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

/**
 * Checks if a word can be moved between lists
 * @param {Array} sourceList - Source list
 * @param {Array} targetList - Target list
 * @param {number} maxTargetSize - Maximum size of target list
 * @returns {boolean} - True if move is allowed
 */
export const canMoveWord = (sourceList, targetList, maxTargetSize = 10) => {
  return sourceList.length > 0 && targetList.length < maxTargetSize;
};

/**
 * Creates a deep copy of an object
 * @param {Object} obj - Object to copy
 * @returns {Object} - Deep copy of the object
 */
export const deepCopy = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Generates a random phone number in format XXX-XXX-XXXX
 * @returns {string} - Random phone number
 */
export const generateRandomPhoneNumber = () => {
  // Generate area code (200-999, excluding some reserved numbers)
  const areaCode = Math.floor(Math.random() * 800) + 200;
  
  // Generate exchange code (200-999)
  const exchangeCode = Math.floor(Math.random() * 800) + 200;
  
  // Generate subscriber number (0000-9999)
  const subscriberNumber = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  
  return `${areaCode}-${exchangeCode}-${subscriberNumber}`;
};

/**
 * Reverses a phone number for backwards entry (digit by digit)
 * @param {string} phoneNumber - Phone number to reverse (format: XXX-XXX-XXXX)
 * @returns {string} - Reversed phone number in same format
 */
export const reversePhoneNumber = (phoneNumber) => {
  // Remove dashes and reverse the digits
  const digitsOnly = phoneNumber.replace(/-/g, '');
  const reversedDigits = digitsOnly.split('').reverse().join('');
  
  // Reformat with dashes in original positions
  return `${reversedDigits.slice(0, 3)}-${reversedDigits.slice(3, 6)}-${reversedDigits.slice(6, 10)}`;
};

/**
 * Debounces a function call
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttles a function call
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Generates a random integer between min and max (inclusive)
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} - Random integer
 */
export const getRandomIntInclusive = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Validates assessment data
 * @param {Object} data - Assessment data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateAssessmentData = (data) => {
  const errors = [];
  
  if (!data.cardData || !Array.isArray(data.cardData)) {
    errors.push('Card data is required and must be an array');
  }
  
  if (!data.responses || !Array.isArray(data.responses)) {
    errors.push('Responses are required and must be an array');
  }
  
  if (data.currentModule === undefined || data.currentModule < -1) {
    errors.push('Current module must be a valid number');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Saves assessment data to localStorage
 * @param {string} key - Storage key
 * @param {Object} data - Data to save
 */
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

/**
 * Loads assessment data from localStorage
 * @param {string} key - Storage key
 * @returns {Object|null} - Loaded data or null if not found
 */
export const loadFromLocalStorage = (key) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return null;
  }
};
